// `<shortlist-extension-alerts>` / `<client-contact-extension-alerts>` — the component ELEMENT
// the review screens' content hangs off.
//
// ⚠️ AN ELEMENT, NOT A CLASS, and the difference is visible. `_extension.state.tabs.scss:99`
// styles them by TYPE:
//
//     shortlist-extension-alerts,
//     client-contact-extension-alerts { border-right: 1px solid #dedede; }
//
// so a `<div className="shortlist-extension-alerts">` — which is what both review routes
// rendered — matches nothing, and the content pane loses its separating border. Same lesson as
// `PageHost`, `ShortlistHeader` and `WooSwitch`: in this codebase the DOM the stylesheet needs
// is not all in the template, and a component element is part of it.
//
// ⚠️ AND IT IS BOTH, NOT EITHER. The element is the component; the CLASS of the same name is
// its template's root div (`shortlist-extension-alerts.tpl.html:1`), and that class is what
// `_shortlist-extension-alerts.scss` hangs almost everything off — including
// `.shortlist-content-container { display: flex; flex: 1 }`. Rendering only the element left
// that container as a block box, so the panel sized itself to its text instead of to the
// frame: 358px wide where production is 200, which then pushed the CV date, the filter totals
// and half the automation banner off the right edge. Two rules, one missing div.
//
// The client-contact template's root carries `client-contact-shortlist-extension-alerts
// shortlist-extension-alerts` — the shared class SECOND, and NOT `client-contact-extension-
// alerts`, which is only ever an element. Reading the stylesheet's `.shortlist-extension-alerts,
// .client-contact-extension-alerts` pair as the two roots is the natural mistake and gives the
// contact panel none of the layout.
import { createElement, type ReactElement, type ReactNode } from 'react'

export interface ExtensionAlertsHostProps {
  /** Which element to render — the two products have their own, styled together. */
  entity?: 'candidates' | 'clientContacts'
  /**
   * `ExtensionViewWidth` in px, as the state's `maxWidth`.
   *
   * ⚠️ THE PANEL'S WIDTH COMES FROM THE ROUTE, NOT FROM THE STYLESHEET, and nothing in the
   * SCSS gives it away: `extension.state.tabs.config.ng1.ts:638` puts
   * `ng-style="::{ 'flex-basis': ctrl.maxWidth }"` on this element and every review state
   * declares `maxWidth = ExtensionViewWidth.Slim`. The stylesheet only says `flex: auto 0 0`,
   * which sizes the panel to its CONTENT — 358px of candidate names at a 700px viewport
   * instead of 200 — and everything then overflows: the CV date, the result count and half the
   * automation banner fall off the right edge, each looking like its own bug.
   */
  flexBasis?: number
  /**
   * `ExtensionViewWidth` in px, as the FULL states' `min-width`.
   *
   * ⚠️ THE EDIT STATE USES A DIFFERENT PROPERTY TO THE REVIEW STATE. Review sets `flex-basis`
   * (a fixed 200px panel beside a side view); edit sets `min-width: 1146px` and lets the panel
   * grow (`extension.state.tabs.config.ng1.ts:504`). Reusing `flex-basis` for both pins the
   * edit screen at a width it is supposed to exceed, and the criteria form then wraps.
   */
  minWidth?: number
  children?: ReactNode
}

export function ExtensionAlertsHost({
  entity = 'candidates',
  flexBasis,
  minWidth,
  children,
}: ExtensionAlertsHostProps): ReactElement {
  const candidates = entity === 'candidates'
  const style = {
    ...(flexBasis === undefined ? {} : { flexBasis: `${String(flexBasis)}px` }),
    ...(minWidth === undefined ? {} : { minWidth: `${String(minWidth)}px` }),
  }
  return createElement(
    candidates ? 'shortlist-extension-alerts' : 'client-contact-extension-alerts',
    Object.keys(style).length === 0 ? null : { style },
    <div
      className={
        candidates
          ? 'shortlist-extension-alerts'
          : 'client-contact-shortlist-extension-alerts shortlist-extension-alerts'
      }
    >
      {children}
    </div>,
  )
}
